import { NgIf, NgFor, AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
} from '@angular/material/list';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { BlockSpinnerDirective, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { downloadCsvTemplate, isCsvFile } from 'src/app/shared/utils/csv-file.utils';
import { DEPARTURE_CSV_HEADERS } from 'src/app/shared/utils/csv-import-headers.constants';

import { Departure } from '../../models/cycle.model';
import {
  Cycle,
  MessageTypeCodeEnum,
  SlaughterReport,
} from '../../models/flock-registration-service.model';
import { FinalSetupNavigationService } from '../../services/final-setup-navigation.service';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle, getSlaughterReports } from '../../store/selectors/cycle.selectors';
import { getDayOldChicks } from '../../store/selectors/day-old-chick.selectors';
import { getDepartures } from '../../store/selectors/departure.selectors';
import { ImportFromSlaughterReportComponent } from '../import-from-slaughter-report/import-from-slaughter-report.component';

@Component({
  selector: 'app-departure',
  templateUrl: 'departure.component.html',
  styleUrls: ['./departure.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatButton,
    MatIcon,
    BlockSpinnerDirective,
    MatNavList,
    NgFor,
    MatListItem,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatListItemMeta,
    MatSlideToggle,
    MatIconButton,
    MatDivider,
    NotificationEmptyComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class DepartureComponent {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly finalSetupNavigationService = inject(FinalSetupNavigationService);
  readonly brandingService = inject(BrandingService);

  departures$: Observable<Departure[]> = this.store.select(getDepartures).pipe(
    skipWhile((d) => !d),
    map((departures) => {
      const sortedDepartures = [...departures].sort(
        (a, b) => new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime(),
      );
      return sortedDepartures;
    }),
  );

  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle).pipe(
    skipWhile((c) => !c?.id),
    take(1),
  );

  slaughterReports$: Observable<SlaughterReport[]> = this.store.select(getSlaughterReports).pipe(
    withLatestFrom(this.cycle$),
    map(([s, cycle]) =>
      s?.filter(
        (sr) =>
          !sr.linkedCycleId &&
          !sr.linkedBarnId &&
          new Date(sr.technicalResult.dateTime) >
            new Date(
              Math.min.apply(
                null,
                cycle.dayOldChicks.map((doc) => new Date(doc.arrivalDate)),
              ),
            ) &&
          sr.header.type === MessageTypeCodeEnum.TechnicalFinancialResults,
      ),
    ),
  );

  hasDayOldChicks$: Observable<boolean> = this.store.pipe(
    select(getDayOldChicks),
    map((dayOldChicks) => !!dayOldChicks && dayOldChicks.length > 0),
  );

  isFinalSetupEnabled$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();
  totalAmountOfDepartedChicks$: Observable<number>;
  percentage$: Observable<number>;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const store = this.store;

    this.cycle$.subscribe((c) => {
      store.dispatch(
        cycleActions.loadSlaughterReportsRequest({
          fromDate: c.cycleDate,
        }),
      );
    });

    this.totalAmountOfDepartedChicks$ = this.departures$.pipe(
      map((departures) =>
        departures.reduce(
          (totalDayOldChicks, departure) => totalDayOldChicks + departure.amountOfChicks,
          0,
        ),
      ),
    );

    this.percentage$ = this.cycle$.pipe(
      withLatestFrom(this.totalAmountOfDepartedChicks$),
      map(([cycle, totalAmountOfDepartedChicks]) => {
        if (cycle.dayOldChicks.length > 0) {
          let totalAmountOfDayOldChicks = 0;

          cycle.dayOldChicks.map((dayOldChick) => {
            totalAmountOfDayOldChicks += dayOldChick.amountOfChicks;
          });

          return Math.round((totalAmountOfDepartedChicks / totalAmountOfDayOldChicks) * 100);
        }
      }),
    );
  }

  add = () => this.router.navigate(['add'], { relativeTo: this.activatedRoute });

  uploadDepartureCsv(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file || !isCsvFile(file)) {
      this.store.dispatch(
        NotificationActions.errorSnackbar({
          message: this.locale.translate('csv-import.invalid-file'),
        }),
      );
      return;
    }

    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.store.dispatch(
        cycleActions.importDeparturesCsvRequest({
          cycleId: cycle.id,
          file,
        }),
      );
    });
  }

  downloadDepartureCsvTemplate() {
    downloadCsvTemplate('departure-import-template.csv', DEPARTURE_CSV_HEADERS);
  }

  import() {
    this.cycle$
      .pipe(withLatestFrom(this.slaughterReports$), take(1))
      .subscribe(([cycle, slaughterReports]) => {
        this.dialogService.fullscreenDialog(
          ImportFromSlaughterReportComponent,
          {
            cycle,
            slaughterReports,
            fullscreen: true,
          },
          {
            maxWidth: 'auto',
          },
        );
      });
  }

  delete(event: Event, id: number) {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.store.pipe(select(getSelectedCycle), take(1)).subscribe((cycle) => {
      this.store.dispatch(
        cycleActions.deleteDepartureWithConfirmation({
          cycleId: cycle.id,
          id,
        }),
      );
    });
  }

  openDepartureEdit(departureId: number) {
    this.router.navigate([departureId], { relativeTo: this.activatedRoute });
  }

  onToggleShowInFinalSetup(departure: Departure, event: MatSlideToggleChange) {
    if (!departure.linkedSlaughterReportId) {
      const updatedDeparture = { ...departure, showInFinalSetup: event.checked };

      this.store
        .select(getSelectedCycle)
        .pipe(take(1))
        .subscribe((cycle) => {
          this.store.dispatch(
            cycleActions.editDeparture({ cycleId: cycle.id, departure: updatedDeparture }),
          );
        });
    }
  }
}
